import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Text,
} from "@a7y/tailwind-catalyst";
import { InfoTooltip } from "@a7y/ui";
import { normalizeAscVendorNumber } from "@a7y/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";

const APPLE_VENDOR_NUMBER_HELP_URL =
  "https://appstoreconnect.apple.com/itc/payments_and_financial_reports#/";

const VENDOR_NUMBER_TOOLTIP_TEXT =
  "Apple Sales & Trends vendor number. Support operators can set/correct this without re-uploading the .p8.";

/**
 * Admin backstop dialog — lets a support operator set/correct the Apple
 * vendor number on a project's ASC store link. Matches the user-facing
 * `EditVendorNumberDialog` behavior (Apple probe, structured error
 * handling) but scoped to the admin API.
 *
 * Invalidates the `['organization-detail', orgId]` query on success so the
 * ProjectsTab re-renders with the new vendor number.
 */
export function EditVendorNumberDialog({
  open,
  projectId,
  orgId,
  currentVendorNumber,
  onClose,
}: {
  open: boolean;
  projectId: string;
  orgId: string;
  currentVendorNumber?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [vendorNumber, setVendorNumber] = useState<string>(
    currentVendorNumber ?? "",
  );
  const [fieldError, setFieldError] = useState<string>("");
  const [genericError, setGenericError] = useState<string>("");

  const mutation = useMutation({
    mutationFn: (value: string) =>
      api.updateProjectAscVendorNumber(projectId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-detail", orgId],
      });
      onClose();
    },
    onError: (err: unknown) => {
      // `createApiClient` throws a stock `Error` with the response body
      // JSON-stringified into its `message` on non-2xx. Parse the tail
      // back out so we can surface the structured error categories.
      const raw = err instanceof Error ? err.message : String(err);
      const detailMatch = raw.match(/ - (\{.+\})$/);
      let parsed:
        | {
            error?: { category?: string; message?: string };
            message?: string;
          }
        | null = null;
      if (detailMatch) {
        try {
          parsed = JSON.parse(detailMatch[1]);
        } catch {
          parsed = null;
        }
      }
      const inner = parsed?.error;
      if (inner?.category === "format") {
        setFieldError(
          inner.message ||
            "Vendor number format is invalid. Enter 6–10 digits (numbers only).",
        );
        return;
      }
      if (inner?.category === "sales_vendor") {
        setFieldError(
          inner.message ||
            "Vendor number is not associated with this App Store Connect account.",
        );
        return;
      }
      setGenericError(
        parsed?.message ||
          raw ||
          "Something went wrong saving the vendor number.",
      );
    },
  });

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError("");
    setGenericError("");
    const normalized = normalizeAscVendorNumber(vendorNumber);
    if (normalized.ok === false) {
      setFieldError(normalized.error);
      return;
    }
    mutation.mutate(normalized.value);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {currentVendorNumber
            ? "Edit Vendor Number (admin)"
            : "Add Vendor Number (admin)"}
        </DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
              Probes Apple&apos;s Sales & Trends API before saving. No
              change to the encrypted .p8 credential.
            </Text>
            <Field>
              <div className="flex items-center gap-1.5">
                <Label>Vendor Number</Label>
                <InfoTooltip
                  text={VENDOR_NUMBER_TOOLTIP_TEXT}
                  linkText="Find this in App Store Connect"
                  linkHref={APPLE_VENDOR_NUMBER_HELP_URL}
                  ariaLabel="About the Apple vendor number"
                />
              </div>
              <Input
                value={vendorNumber}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setVendorNumber(e.target.value)
                }
                inputMode="numeric"
                pattern="\d*"
                placeholder="e.g. 1234567"
                disabled={mutation.isPending}
                aria-label="Vendor Number"
                aria-invalid={fieldError ? "true" : undefined}
                autoFocus
              />
              {fieldError && (
                <div className="text-red-600 text-sm mt-1" role="alert">
                  {fieldError}
                </div>
              )}
            </Field>
            {genericError && (
              <div
                className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/40 dark:text-red-200"
                role="alert"
              >
                {genericError}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" color="blue" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
